import {
	DashboardIndicators as BasiliskDashboardIndicators,
	DashboardJobRow as BasiliskDashboardJobRow,
	DashboardJobStatus as BasiliskDashboardJobStatus,
	DashboardResponse as BasiliskDashboardResponse,
} from 'modules/basilisk/pages/dashboard/dashboard.model';
import {
	DashboardIndicators as CadastroDashboardIndicators,
	DashboardJobRow as CadastroDashboardJobRow,
	DashboardResponse as CadastroDashboardResponse,
} from 'modules/cadastro/pages/dashboard/dashboard.model';
import {
	DashboardIndicators as ContestacaoDashboardIndicators,
	DashboardJobRow as ContestacaoDashboardJobRow,
	DashboardJobStatus as ContestacaoDashboardJobStatus,
	DashboardJobUser,
	DashboardResponse as ContestacaoDashboardResponse,
} from 'modules/contestacao/pages/dashboard/dashboard.model';
import { JobProductSlug, JobStatus, JobStatusLabel, JobStatusType } from 'services/jobs/jobs.model';
import { formatDurationMs } from 'utils/dashboard.util';
import { productSetupsDb } from '../context/context.const';
import { MOCK_PEOPLE } from '../shared/mock-context.const';
import { MockJobView, productOfSetup, queryJobViews, sessionContext } from '../jobs/jobs.const';

/**
 * Dashboards (GET /dashboards/:produto/job) montados a partir do MESMO estado dos
 * jobs da caixa de entrada — os números batem com a lista e com o overview.
 */

export interface DashboardFilters {
	startDate: string;
	endDate: string;
	userId: string | null;
}

type ReviewDashboardStatus = ContestacaoDashboardJobStatus & BasiliskDashboardJobStatus;

const REVIEW_STATUS: Record<JobStatusLabel, ReviewDashboardStatus> = {
	Aguardando: 'AWAITING',
	'Em processamento': 'PROCESSING',
	'Aguardando revisão': 'AWAITING_REVIEW',
	'Em revisão': 'VALIDATING',
	Finalizado: 'FINISHED',
	Falha: 'FAILED',
};

const CADASTRO_STATUS: Record<JobStatusLabel, JobStatusType> = {
	Aguardando: JobStatus.AWAITING,
	'Em processamento': JobStatus.PROCESSING,
	'Aguardando revisão': JobStatus.PROCESSING,
	'Em revisão': JobStatus.PROCESSING,
	Finalizado: JobStatus.FINISHED,
	Falha: JobStatus.FAILED,
};

function viewsInPeriod(product: JobProductSlug, filters: DashboardFilters): MockJobView[] {
	const fromMs = new Date(filters.startDate).getTime();
	const toMs = new Date(filters.endDate).getTime();
	// O Hub não manda o setup no dashboard: a API usa o contexto ativo da sessão (switch-context)
	const activeSetupId = sessionContext().productSetupId;
	return queryJobViews({
		product,
		productSetupId: productOfSetup(activeSetupId) === product ? activeSetupId : null,
		createdByUserId: filters.userId,
		fromMs: Number.isNaN(fromMs) ? null : fromMs,
		toMs: Number.isNaN(toMs) ? null : toMs,
	});
}

function processingMsOf(view: MockJobView): number {
	return view.timings.processingTimeMs ?? 0;
}

function countBy(views: MockJobView[], status: JobStatusLabel): number {
	return views.filter((view) => view.status === status).length;
}

function baseIndicators(views: MockJobView[]): CadastroDashboardIndicators {
	const measured = views.map(processingMsOf).filter((ms) => ms > 0);
	const averageMs = measured.length
		? Math.round(measured.reduce((total, ms) => total + ms, 0) / measured.length)
		: 0;
	const maxMs = measured.length ? Math.max(...measured) : 0;
	const failedJobs = countBy(views, 'Falha');

	return {
		finishedJobs: countBy(views, 'Finalizado'),
		failedJobs,
		awaitingJobs: countBy(views, 'Aguardando'),
		processingJobs: countBy(views, 'Em processamento'),
		jobFailureRatio: views.length ? failedJobs / views.length : 0,
		averageProcessingTimeMs: averageMs,
		averageProcessingTimeLabel: formatDurationMs(averageMs),
		maxProcessingTimeMs: maxMs,
		maxProcessingTimeLabel: formatDurationMs(maxMs),
	};
}

function reviewIndicators(
	views: MockJobView[],
): ContestacaoDashboardIndicators & BasiliskDashboardIndicators {
	return {
		...baseIndicators(views),
		awaitingReviewJobs: countBy(views, 'Aguardando revisão'),
		validatingJobs: countBy(views, 'Em revisão'),
	};
}

function dashboardUser(view: MockJobView): DashboardJobUser {
	const person = Object.values(MOCK_PEOPLE).find((candidate) => candidate.id === view.person.id);
	return { ...view.person, image: person?.image ?? null };
}

function lastUpdateOf(view: MockJobView): string {
	return view.finishedAt ?? view.processedAt ?? view.startedAt ?? view.createdAt;
}

function customPromptIdOf(productSetupId: string): string | null {
	return (
		productSetupsDb.find((setup) => setup.id === productSetupId)?.productCustomPromptId ?? null
	);
}

interface DashboardRowCommon {
	id: string;
	description: string | null;
	productSetupId: string;
	productCustomPromptId: string | null;
	billingEntryId: string | null;
	source: MockJobView['entry']['source'];
	idempotencyKey: string;
	retryCount: number;
	retryScheduledAt: string | null;
	pagesProcessed: number;
	isOverLimit: boolean;
	startedAt: string | null;
	lastProcessingStartedAt: string | null;
	extractionCompletedAt: string | null;
	finishedAt: string | null;
	failureReason: string | null;
	failureDetails: null;
	createdAt: string;
	updatedAt: string;
	deletedAt: string | null;
	processingTimeMs: number;
	processingTimeLabel: string;
}

function rowCommon(view: MockJobView): DashboardRowCommon {
	const processingTimeMs = processingMsOf(view);
	return {
		id: view.entry.id,
		description: view.entry.description,
		productSetupId: view.entry.productSetupId,
		productCustomPromptId: customPromptIdOf(view.entry.productSetupId),
		billingEntryId:
			view.processedAt && view.status !== 'Falha' ? `bill-entry-${view.entry.id}` : null,
		source: view.entry.source,
		idempotencyKey: `idem-${view.entry.id}`,
		retryCount: view.retryCount,
		retryScheduledAt: null,
		pagesProcessed: view.pagesProcessed,
		isOverLimit: false,
		startedAt: view.startedAt,
		lastProcessingStartedAt: view.startedAt,
		extractionCompletedAt: view.status === 'Falha' ? null : view.processedAt,
		finishedAt: view.finishedAt,
		failureReason: view.failure?.message ?? null,
		failureDetails: null,
		createdAt: view.createdAt,
		updatedAt: lastUpdateOf(view),
		deletedAt: null,
		processingTimeMs,
		processingTimeLabel: formatDurationMs(processingTimeMs),
	};
}

export function buildCadastroDashboard(filters: DashboardFilters): CadastroDashboardResponse {
	const views = viewsInPeriod('cadastro', filters);
	const rows: CadastroDashboardJobRow[] = views.map((view) => ({
		...rowCommon(view),
		status: CADASTRO_STATUS[view.status],
		createdBy: view.person.id,
		updatedBy: view.person.id,
		deletedBy: null,
	}));
	return {
		referencePeriod: { startDate: filters.startDate, endDate: filters.endDate },
		indicators: baseIndicators(views),
		rows,
	};
}

export function buildContestacaoDashboard(filters: DashboardFilters): ContestacaoDashboardResponse {
	const views = viewsInPeriod('contestacao', filters);
	const rows: ContestacaoDashboardJobRow[] = views.map((view) => ({
		...rowCommon(view),
		status: REVIEW_STATUS[view.status],
		createdBy: dashboardUser(view),
		updatedBy: dashboardUser(view),
		deletedBy: null,
	}));
	return {
		referencePeriod: { startDate: filters.startDate, endDate: filters.endDate },
		indicators: reviewIndicators(views),
		rows,
	};
}

export function buildBasiliskDashboard(filters: DashboardFilters): BasiliskDashboardResponse {
	const views = viewsInPeriod('basilisk', filters);
	const rows: BasiliskDashboardJobRow[] = views.map((view) => {
		const processingTimeMs = processingMsOf(view);
		return {
			id: view.entry.id,
			description: view.entry.description,
			status: REVIEW_STATUS[view.status],
			source: view.entry.source,
			startedAt: view.startedAt,
			finishedAt: view.finishedAt,
			failureReason: view.failure?.message ?? null,
			createdAt: view.createdAt,
			// A API do dashboard do Basilisk devolve só o id do criador, sem expandir o usuário.
			createdBy: view.person.id,
			processingTimeMs,
			processingTimeLabel: formatDurationMs(processingTimeMs),
			metadata: { ...view.profile.metadata },
		};
	});
	return {
		referencePeriod: { startDate: filters.startDate, endDate: filters.endDate },
		indicators: reviewIndicators(views),
		rows,
	};
}
